import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useData, useApp } from '../context.jsx';
import { getTodayKey, getMonthKey, addMonths, advanceDueDate } from '../lib/helpers.js';
import { MONTH_NAMES, TRANSACTION_TYPES } from '../lib/constants.js';
import { IconButton, Progress } from '../components/ui.jsx';

export default function Budget() {
  const { data, updateData } = useData();
  const { removeItem } = useApp();

  const availableMonths = useMemo(() => {
    const months = [...new Set(data.budgetCategories.map((cat) => cat.month))];
    return months.sort((a, b) => {
      const [an, ay] = a.split(' ');
      const [bn, by] = b.split(' ');
      return (parseInt(ay, 10) * 12 + MONTH_NAMES.indexOf(an)) - (parseInt(by, 10) * 12 + MONTH_NAMES.indexOf(bn));
    });
  }, [data.budgetCategories]);

  const [activeMonth, setActiveMonth] = useState(() => {
    const current = getMonthKey();
    const months = [...new Set(data.budgetCategories.map((cat) => cat.month))];
    return months.includes(current) ? current : (months[months.length - 1] || current);
  });

  const [txForm, setTxForm] = useState({ label: '', amount: '', date: getTodayKey(), budgetCategoryId: '', type: 'expense' });

  const prevMonth = useMemo(() => {
    const idx = availableMonths.indexOf(activeMonth);
    return idx > 0 ? availableMonths[idx - 1] : null;
  }, [availableMonths, activeMonth]);

  const nextMonthKey = addMonths(activeMonth, 1);
  const nextMonthExists = availableMonths.includes(nextMonthKey);

  const monthCategories = useMemo(() => (
    data.budgetCategories.filter((cat) => cat.month === activeMonth)
  ), [data.budgetCategories, activeMonth]);

  const monthTransactions = useMemo(() => (
    data.transactions.filter((tx) => {
      if (!tx.date) return false;
      return getMonthKey(new Date(tx.date + 'T00:00:00')) === activeMonth;
    })
  ), [data.transactions, activeMonth]);

  const categoryActuals = useMemo(() => (
    monthTransactions.reduce((map, tx) => {
      if (!tx.budgetCategoryId) return map;
      map[tx.budgetCategoryId] = (map[tx.budgetCategoryId] || 0) + Number(tx.amount);
      return map;
    }, {})
  ), [monthTransactions]);

  const categoryMap = useMemo(() => (
    Object.fromEntries(data.budgetCategories.map((cat) => [cat.id, cat]))
  ), [data.budgetCategories]);

  const totals = useMemo(() => (
    monthCategories.reduce((sum, item) => ({
      budgeted: sum.budgeted + Number(item.budgeted),
      actual: sum.actual + (categoryActuals[item.id] || 0)
    }), { budgeted: 0, actual: 0 })
  ), [monthCategories, categoryActuals]);

  const monthExpenses = useMemo(() => (
    monthTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + Number(tx.amount), 0)
  ), [monthTransactions]);

  const sortedTransactions = useMemo(() => (
    [...monthTransactions].sort((a, b) => b.date.localeCompare(a.date))
  ), [monthTransactions]);

  const addCategory = () => {
    updateData((current) => ({
      ...current,
      budgetCategories: [...current.budgetCategories, {
        id: crypto.randomUUID(),
        month: activeMonth,
        category: '',
        subcategory: '',
        budgeted: 0,
        actual: 0,
        transactionType: 'Fixed',
        expectedDue: ''
      }]
    }));
  };

  const deleteCategory = (id) => {
    updateData((current) => ({
      ...current,
      budgetCategories: current.budgetCategories.filter((cat) => cat.id !== id)
    }));
  };

  const updateBudgetCategory = (id, key, value) => {
    updateData((current) => ({
      ...current,
      budgetCategories: current.budgetCategories.map((item) => (
        item.id === id
          ? { ...item, [key]: key === 'budgeted' ? Number(value) || 0 : value }
          : item
      ))
    }));
  };

  const addTransaction = () => {
    const label = txForm.label.trim();
    const amount = parseFloat(txForm.amount);
    if (!label || isNaN(amount) || amount <= 0) return;
    updateData((current) => ({
      ...current,
      transactions: [
        { id: crypto.randomUUID(), label, amount, type: txForm.type, date: txForm.date || getTodayKey(), budgetCategoryId: txForm.budgetCategoryId || null },
        ...current.transactions
      ]
    }));
    setTxForm((f) => ({ ...f, label: '', amount: '' }));
  };

  const addNextMonth = () => {
    if (nextMonthExists) { setActiveMonth(nextMonthKey); return; }
    const newCategories = monthCategories.map((cat) => ({
      ...cat,
      id: crypto.randomUUID(),
      month: nextMonthKey,
      actual: 0,
      expectedDue: advanceDueDate(cat.expectedDue, activeMonth, nextMonthKey)
    }));
    updateData((current) => ({ ...current, budgetCategories: [...current.budgetCategories, ...newCategories] }));
    setActiveMonth(nextMonthKey);
  };

  return (
    <section className="view-grid">
      <section className="panel wide">
        <div className="budget-inputs">
          <label>Monthly budget <input value={data.monthlyBudget} onChange={(e) => updateData((c) => ({ ...c, monthlyBudget: Number(e.target.value) || 0 }))} type="number" /></label>
          <label>Weekly budget <input value={data.weeklyBudget} onChange={(e) => updateData((c) => ({ ...c, weeklyBudget: Number(e.target.value) || 0 }))} type="number" /></label>
        </div>
        <Progress label="Monthly spending" value={monthExpenses} max={data.monthlyBudget} />
        <Progress label="Weekly estimate" value={monthExpenses / 4} max={data.weeklyBudget} />
      </section>

      <section className="panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Budget categories</p>
            <div className="month-nav">
              <button className="icon-button" onClick={() => prevMonth && setActiveMonth(prevMonth)} disabled={!prevMonth} aria-label="Previous month" type="button">
                <ChevronLeft size={16} />
              </button>
              <select className="month-select" value={activeMonth} onChange={(e) => setActiveMonth(e.target.value)} aria-label="Select month">
                {availableMonths.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button className="icon-button" onClick={() => nextMonthExists && setActiveMonth(nextMonthKey)} disabled={!nextMonthExists} aria-label="Next month" type="button">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <div className="budget-month-controls">
            <div className="budget-total-pill">
              <span>Budget ${Number(data.monthlyBudget).toFixed(2)}</span>
              <strong>Actual ${totals.actual.toFixed(2)}</strong>
            </div>
            <button className="text-button" onClick={addNextMonth} type="button">
              <Plus size={14} /> {nextMonthExists ? nextMonthKey : `Copy to ${nextMonthKey}`}
            </button>
          </div>
        </div>
        {monthCategories.length === 0 ? (
          <p className="empty-drop-text">No categories for {activeMonth}.</p>
        ) : (
          <div className="budget-category-table">
            <div className="budget-category-header">
              <span>Category</span>
              <span>Subcategory</span>
              <span>Budgeted</span>
              <span>Actual</span>
              <span>Difference</span>
              <span>Type</span>
              <span>Due</span>
              <span />
            </div>
            {monthCategories.map((item) => {
              const actual = categoryActuals[item.id] || 0;
              const difference = Number(item.budgeted) - actual;
              return (
                <div className={difference < 0 ? 'budget-category-row over' : 'budget-category-row'} key={item.id}>
                  <input value={item.category} onChange={(e) => updateBudgetCategory(item.id, 'category', e.target.value)} aria-label="Category" />
                  <input value={item.subcategory} onChange={(e) => updateBudgetCategory(item.id, 'subcategory', e.target.value)} aria-label="Subcategory" />
                  <input value={item.budgeted} onChange={(e) => updateBudgetCategory(item.id, 'budgeted', e.target.value)} type="number" aria-label="Budgeted amount" />
                  <strong className="budget-actual">${actual.toFixed(2)}</strong>
                  <strong className="budget-diff">{difference < 0 ? '-' : ''}${Math.abs(difference).toFixed(2)}</strong>
                  <select value={item.transactionType} onChange={(e) => updateBudgetCategory(item.id, 'transactionType', e.target.value)} aria-label="Transaction type">
                    {TRANSACTION_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                  <input value={item.expectedDue} onChange={(e) => updateBudgetCategory(item.id, 'expectedDue', e.target.value)} type="date" aria-label="Expected due" />
                  <IconButton icon={Trash2} label="Delete category" onClick={() => deleteCategory(item.id)} />
                </div>
              );
            })}
          </div>
        )}
        <button className="budget-add-category" onClick={addCategory} type="button">
          <Plus size={14} /> Add category
        </button>
      </section>

      <section className="transaction-form wide">
        <label>
          Description
          <input value={txForm.label} onChange={(e) => setTxForm((f) => ({ ...f, label: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && addTransaction()} placeholder="e.g. Electric bill" />
        </label>
        <label>
          Amount
          <input value={txForm.amount} onChange={(e) => setTxForm((f) => ({ ...f, amount: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && addTransaction()} type="number" min="0" step="0.01" placeholder="0.00" />
        </label>
        <label>
          Date
          <input value={txForm.date} onChange={(e) => setTxForm((f) => ({ ...f, date: e.target.value }))} type="date" />
        </label>
        <label>
          Budget category
          <select value={txForm.budgetCategoryId} onChange={(e) => setTxForm((f) => ({ ...f, budgetCategoryId: e.target.value }))}>
            <option value="">— none —</option>
            {availableMonths.map((month) => (
              <optgroup key={month} label={month}>
                {data.budgetCategories.filter((cat) => cat.month === month).map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.subcategory}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label>
          Type
          <select value={txForm.type} onChange={(e) => setTxForm((f) => ({ ...f, type: e.target.value }))}>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
        </label>
        <button className="primary-button" onClick={addTransaction} type="button"><Plus size={16} /> Add</button>
      </section>

      <section className="panel wide">
        <div className="section-heading">
          <h3>Transaction log</h3>
          <span className="eyebrow">{activeMonth}</span>
        </div>
        {sortedTransactions.length === 0 ? (
          <p className="empty-drop-text">No transactions for {activeMonth}.</p>
        ) : (
          <div className="list-table">
            {sortedTransactions.map((item) => (
              <div className="table-row" key={item.id}>
                <span className="tx-date">{item.date}</span>
                <span>{item.label}</span>
                <span>{item.budgetCategoryId ? (categoryMap[item.budgetCategoryId]?.subcategory || '—') : '—'}</span>
                <strong className={item.type === 'income' ? 'tx-income' : 'tx-expense'}>{item.type === 'income' ? '+' : '-'}${Number(item.amount).toFixed(2)}</strong>
                <IconButton label="Remove" icon={Trash2} onClick={() => removeItem('transactions', item.id)} />
              </div>
            ))}
          </div>
        )}
      </section>
    </section>
  );
}
